import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Rocket, X, ChevronRight, CheckCircle2, Circle, PartyPopper,
} from "lucide-react";
import api from "../../lib/api";

/**
 * Floating Onboarding (Stripe-style) — replaces the in-flow OnboardingChecklist
 * card that used to sit at the top of the Dashboard.
 *
 * Design rationale (Feb 2026):
 *  - The old card pushed all actionable content below the fold even for
 *    restaurants that hadn't started using it yet.
 *  - This version is a collapsed pill in the bottom-left corner (sibling
 *    of the chatbot in bottom-right but on the opposite side so they don't
 *    compete visually).
 *  - Click expands a vertical panel above the pill with the checklist.
 *  - When all 5 tasks are done, the component shows a one-time celebration
 *    toast for 4 seconds then unmounts itself permanently (we persist
 *    `mg_onboarding_done` in localStorage so it never re-appears).
 *  - Auto-collapsed state remembered in localStorage too — power users
 *    can keep it hidden between sessions.
 *
 * Position:
 *  - bottom-left (`bottom-6 left-6`) on desktop, same on mobile.
 *  - Avoids the chatbot (bottom-right) and the IA assistant FAB (bottom-24
 *    right-6) — no overlap on any viewport.
 */
const CHECKLIST_ITEMS = [
  { key: "restaurant_setup", label: "Completa los datos del restaurante", path: "/app/settings" },
  { key: "first_menu",       label: "Crea tu primer menu",                path: "/app/menus" },
  { key: "dish_images",      label: "Genera imagenes de platos con IA",   path: "/app/recipes" },
  { key: "website",          label: "Activa tu pagina web",               path: "/app/web" },
  { key: "team_invite",      label: "Invita a un miembro del equipo",     path: "/app/team" },
];

const LS_DISMISSED = "mg_onboarding_floating_dismissed";
const LS_DONE = "mg_onboarding_floating_done";

export default function FloatingOnboarding() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [permanentlyHidden, setPermanentlyHidden] = useState(() => {
    try { return localStorage.getItem(LS_DONE) === "1"; }
    catch { return false; }
  });
  const [softDismissed, setSoftDismissed] = useState(() => {
    try { return localStorage.getItem(LS_DISMISSED) === "1"; }
    catch { return false; }
  });
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    if (permanentlyHidden) { setLoading(false); return; }
    api.get("/help/onboarding")
      .then(d => setProgress(d))
      .catch(() => { /* silent — backend unavailable shouldn't break the page */ })
      .finally(() => setLoading(false));
  }, [permanentlyHidden]);

  const toggleStep = async (key) => {
    const current = progress?.steps?.[key] || false;
    try {
      const updated = await api.post("/help/onboarding", { step: key, completed: !current });
      setProgress(updated);
      // If all steps just got completed → celebrate then auto-hide forever.
      const allDone = CHECKLIST_ITEMS.every(i => updated.steps?.[i.key]);
      if (allDone) celebrateAndHide();
    } catch { /* ignore */ }
  };

  const celebrateAndHide = () => {
    setExpanded(false);
    setShowCelebration(true);
    setTimeout(() => {
      try { localStorage.setItem(LS_DONE, "1"); } catch { /* ignore */ }
      setPermanentlyHidden(true);
    }, 4000);
  };

  const handleSoftDismiss = (e) => {
    e?.stopPropagation();
    try { localStorage.setItem(LS_DISMISSED, "1"); } catch { /* ignore */ }
    setSoftDismissed(true);
  };

  // ── Early exits ─────────────────────────────────────────────────────────
  if (loading || permanentlyHidden) return null;

  // If the user already finished everything but never reloaded the page in
  // a session where we'd celebrate, just hide silently.
  if (progress && CHECKLIST_ITEMS.every(i => progress.steps?.[i.key])) {
    if (!showCelebration) {
      try { localStorage.setItem(LS_DONE, "1"); } catch { /* ignore */ }
      return null;
    }
  }
  if (!progress) return null;

  const completedCount = CHECKLIST_ITEMS.filter(i => progress.steps?.[i.key]).length;
  const total = CHECKLIST_ITEMS.length;
  const pct = Math.round((completedCount / total) * 100);

  // Soft-dismissed: show only a tiny restore chip so the user can bring it
  // back without losing the feature entirely.
  if (softDismissed && !showCelebration) {
    return (
      <button
        type="button"
        onClick={() => { setSoftDismissed(false); try { localStorage.removeItem(LS_DISMISSED); } catch { /* ignore */ } }}
        className="fixed bottom-6 left-6 z-30 h-10 px-3 rounded-full bg-card/80 backdrop-blur
                   border border-border text-xs text-muted-foreground hover:text-foreground
                   shadow-sm hover:shadow transition flex items-center gap-1.5" /* z-stack-doc: L3 onboarding restore pill, paired with sticky topbar */
        data-testid="onboarding-floating-restore"
      >
        <Rocket className="w-3.5 h-3.5" />
        Onboarding · {completedCount}/{total}
      </button>
    );
  }

  // Celebration overlay (4s after the last step is checked).
  if (showCelebration) {
    return (
      <div
        className="fixed bottom-6 left-6 z-40 w-[320px] max-w-[calc(100vw-3rem)] rounded-2xl
                   bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-5 shadow-2xl
                   animate-in fade-in slide-in-from-bottom-4 duration-500" /* z-stack-doc: L4 celebration toast, transient (4s) */
        data-testid="onboarding-celebration"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/20 rounded-lg">
            <PartyPopper className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg leading-tight">Listo!</h3>
            <p className="text-sm text-white/90 mt-0.5">
              Has completado la configuración inicial. ¡Disfruta MenuGenius!
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Collapsed pill (always visible until dismissed) ──────── */}
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          aria-label="Abrir guía de bienvenida"
          className="fixed bottom-6 left-6 z-30 h-12 px-4 rounded-full bg-card border border-border
                     shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition
                     flex items-center gap-2.5 text-sm
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" /* z-stack-doc: L3 onboarding collapsed pill */
          data-testid="onboarding-floating-pill"
        >
          <div className="relative h-6 w-6 shrink-0">
            <svg viewBox="0 0 24 24" className="absolute inset-0 -rotate-90">
              <circle cx="12" cy="12" r="10" strokeWidth="3" fill="none" className="stroke-muted" />
              <circle
                cx="12" cy="12" r="10" strokeWidth="3" fill="none"
                strokeDasharray={`${(pct / 100) * 62.8} 62.8`}
                strokeLinecap="round"
                className="stroke-primary transition-all duration-700"
              />
            </svg>
            <Rocket className="absolute inset-0 m-auto w-3 h-3 text-primary" />
          </div>
          <span className="font-medium">Configura tu restaurante</span>
          <span className="text-[11px] font-semibold text-primary tabular-nums bg-primary/10 px-1.5 py-0.5 rounded-full">
            {completedCount}/{total}
          </span>
          <X
            className="w-3.5 h-3.5 text-muted-foreground/60 hover:text-foreground transition cursor-pointer ml-1"
            onClick={handleSoftDismiss}
            data-testid="onboarding-floating-soft-dismiss"
          />
        </button>
      )}

      {/* ── Expanded panel ────────────────────────────────────────── */}
      {expanded && (
        <div
          className="fixed bottom-6 left-6 z-40 w-[340px] max-w-[calc(100vw-3rem)] rounded-2xl
                     bg-card border border-border shadow-2xl
                     animate-in fade-in slide-in-from-bottom-4 duration-200" /* z-stack-doc: L4 onboarding expanded panel, dismissable */
          data-testid="onboarding-floating-panel"
        >
          {/* Header */}
          <div className="p-4 border-b border-border flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Rocket className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-bold text-base leading-tight">Configura tu restaurante</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {completedCount} de {total} pasos · {pct}% completado
              </p>
              <div className="h-1.5 bg-muted rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="text-muted-foreground hover:text-foreground shrink-0"
              aria-label="Minimizar"
              data-testid="onboarding-floating-collapse"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Steps */}
          <div className="p-2 max-h-[60vh] overflow-y-auto">
            {CHECKLIST_ITEMS.map(item => {
              const done = progress.steps?.[item.key] || false;
              return (
                <div
                  key={item.key}
                  className="flex items-center gap-3 group p-2 rounded-lg hover:bg-muted/40 transition"
                  data-testid={`floating-checklist-${item.key}`}
                >
                  <button
                    onClick={() => toggleStep(item.key)}
                    className="flex-shrink-0 transition-transform hover:scale-110"
                    aria-label={done ? "Marcar como pendiente" : "Marcar como completado"}
                  >
                    {done
                      ? <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                      : <Circle className="w-5 h-5 text-muted-foreground/40" />
                    }
                  </button>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`flex-1 text-left text-sm transition-colors ${
                      done ? "text-muted-foreground line-through" : "text-foreground group-hover:text-primary"
                    }`}
                  >
                    {item.label}
                  </button>
                  {!done && (
                    <ChevronRight
                      className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition cursor-pointer"
                      onClick={() => navigate(item.path)}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-border flex items-center justify-between">
            <button
              type="button"
              onClick={handleSoftDismiss}
              className="text-xs text-muted-foreground hover:text-foreground transition"
              data-testid="onboarding-floating-soft-dismiss-footer"
            >
              Ocultar
            </button>
            <span className="text-[11px] text-muted-foreground">
              Se ocultará al completar todo
            </span>
          </div>
        </div>
      )}
    </>
  );
}
